import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SmsIcon from '@mui/icons-material/Sms';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import React from 'react';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '@/components/LoadingButton';
import { useStructures } from '@/Structures/useStructures';
import { naturalCompare } from '@/util';

import { getUserAccount, updateAccount, updateUserAccountSettings } from '../api';
import { IAccountWithUserAccounts, IUserAccountBase, NotificationFlags } from '../types';

// Import TimeZoneSelector
const TimeZoneSelector = React.lazy(() => import('@/components/TimeZoneSelector'));

// ---- Labels & columns ----
const NOTIFICATION_FLAG_LABELS: Record<NotificationFlags, string> = {
  [NotificationFlags.METER_HEALTH_SUMMARY]: 'Device Health Summary',
  // [NotificationFlags.INSTALL_HEALTH_SUMMARY]: 'Install Health Summary',
  [NotificationFlags.LEAK]: 'Leak Alerts',
  [NotificationFlags.HIGH_USAGE]: 'High-Flow Leak Alerts',
  // [NotificationFlags.MESSAGES_SEEN_EVENT]: 'Messages Seen Event',
  // [NotificationFlags.METER_MODE_ACHIEVED_EVENT]: 'Meter Mode Achieved Event',
  [NotificationFlags.INITIAL_METER_HEALTHY_EVENT]: 'Initial Activation Alerts',
  [NotificationFlags.METER_UNHEALTHY_EVENT]: 'Device Unhealthy Events',
  [NotificationFlags.METER_HEALTHY_EVENT]: 'Device Healthy Events',
};

// ---- Labels & columns ----
const NOTIFICATION_FLAG_DESCRIPTIONS: Record<NotificationFlags, string> = {
  [NotificationFlags.METER_HEALTH_SUMMARY]:
    'Weekly summary of device health and water usage across all properties on the account, ' +
    'regardless of Alert Properties.',
  // [NotificationFlags.INSTALL_HEALTH_SUMMARY]: 'Install Health Summary',
  [NotificationFlags.LEAK]: 'Sent when a low flow, continuous leak or abnormal usage is detected.',
  [NotificationFlags.HIGH_USAGE]:
    'Sent when a high flow leak with high costs or damage is detected.',
  // [NotificationFlags.MESSAGES_SEEN_EVENT]: 'Messages Seen Event',
  // [NotificationFlags.METER_MODE_ACHIEVED_EVENT]: 'Meter Mode Achieved Event',
  [NotificationFlags.INITIAL_METER_HEALTHY_EVENT]:
    'Sent to confirm successful installation of a meter.',
  [NotificationFlags.METER_UNHEALTHY_EVENT]:
    'Sent when a meter is malfunctioning. Please contact support.',
  [NotificationFlags.METER_HEALTHY_EVENT]: 'Sent when a meter is functioning correctly again.',
};

const FLAG_ORDER: NotificationFlags[] = [
  NotificationFlags.METER_HEALTH_SUMMARY,
  NotificationFlags.LEAK,
  NotificationFlags.HIGH_USAGE,
  NotificationFlags.INITIAL_METER_HEALTHY_EVENT,
  NotificationFlags.METER_UNHEALTHY_EVENT,
  NotificationFlags.METER_HEALTHY_EVENT,
];

const ALERT_PROPERTIES_LABEL = 'Alert Properties';
const ALERT_PROPERTIES_DESCRIPTION =
  'Which properties this user receives device alerts for. Everyone starts with all properties. ' +
  'A user limited to specific properties will not receive alerts for properties added later ' +
  'until those are selected here. The Device Health Summary always covers every property on the account.';
const ALL_PROPERTIES_TEXT = 'All properties';
// Above this many selected properties the cell shows a count instead of names
const MAX_NAMED_PROPERTIES = 2;

const sameIdSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

// Day-of-week helpers. IMPORTANT: ensure backend expects the same mapping.
// If your API uses 0=Sunday..6=Saturday, keep this. If different, adjust map below.
const DOW_LABELS: { value: number; label: string }[] = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// ---------------- Component ----------------
interface UserAlertsViewProps {
  account: IAccountWithUserAccounts;
  currentUserId?: string;
  onRefreshAccount?: () => Promise<void>;
}

export function UserAlertsView({ account, currentUserId, onRefreshAccount }: UserAlertsViewProps) {
  // ---- Table paging ----
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // ---- Per-user rows ----
  type Row = {
    joinId: string; // userAccount.id (join record id)
    userId: string; // userAccount.user_id (actual user id)
    email: string;
    note: string | null;
    user_status: string;
    enabled_email_notifications: NotificationFlags[];
    enabled_sms_notifications: NotificationFlags[];
    // Properties this user receives device alerts for; [] means all of them. The account
    // payload does not carry it, so it is only trusted once the row has loaded.
    alert_structure_ids: string[];
    loading: boolean;
    saving: boolean;
    error?: string | null;
  };

  const confirmedUsers = React.useMemo(
    () =>
      account.associated_user_accounts.filter(
        (u) => u.user_status === 'CONFIRMED' || u.user_status === 'FORCE_CHANGE_PASSWORD',
      ),
    [account.associated_user_accounts],
  );

  // seed rows from the account payload, then lazy-refresh each join record via getUserAccount
  // Sort by email address alphabetically when first rendering
  const [rows, setRows] = React.useState<Row[]>(() =>
    confirmedUsers
      .map((u) => ({
        joinId: u.id,
        userId: u.user_id,
        email: u.email,
        note: u.note,
        user_status: u.user_status,
        enabled_email_notifications: u.enabled_email_notifications || [],
        enabled_sms_notifications: u.enabled_sms_notifications || [],
        alert_structure_ids: [],
        loading: true,
        saving: false,
        error: null,
      }))
      .sort((a, b) => naturalCompare(a.email, b.email)),
  );

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // load each user’s freshest settings (in parallel, with throttling if you prefer)
        await Promise.all(
          rows.map(async (row) => {
            try {
              const latest: IUserAccountBase = await getUserAccount(row.joinId);
              if (!alive) return;
              setRows((prev) =>
                prev.map((r) =>
                  r.joinId === row.joinId
                    ? {
                        ...r,
                        enabled_email_notifications: latest.enabled_email_notifications || [],
                        enabled_sms_notifications: latest.enabled_sms_notifications || [],
                        alert_structure_ids: latest.alert_structure_ids || [],
                        note: latest.note,
                        loading: false,
                        error: null,
                      }
                    : r,
                ),
              );
            } catch (e: any) {
              if (!alive) return;
              setRows((prev) =>
                prev.map((r) =>
                  r.joinId === row.joinId
                    ? { ...r, loading: false, error: e?.message || 'Failed to load' }
                    : r,
                ),
              );
            }
          }),
        );
      } catch {
        // no-op — row-level errors handled above
      }
    };
    load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Properties for the Alert Properties column ----
  const {
    structures: loadedStructures,
    isLoading: structuresLoading,
    error: structuresError,
  } = useStructures();
  const structures = React.useMemo(
    () => [...(loadedStructures || [])].sort((a, b) => naturalCompare(a.name, b.name)),
    [loadedStructures],
  );

  const describeScope = (ids: string[]) => {
    if (ids.length === 0) return ALL_PROPERTIES_TEXT;
    const names = structures
      .filter((structure) => ids.includes(structure.id))
      .map((structure) => structure.name);
    // Fall back to a count when there are too many names to show, or when a scoped id has no
    // name in the loaded list (a property deleted since the list was cached)
    if (names.length === ids.length && names.length <= MAX_NAMED_PROPERTIES) {
      return names.join(', ');
    }
    return `${ids.length} ${ids.length === 1 ? 'property' : 'properties'}`;
  };

  // The open selector edits a draft; the draft is saved once when the selector closes, so a
  // user picking several properties makes one request and gets one toast.
  type ScopeDraft = { joinId: string; ids: string[] };
  const [scopeDraft, setScopeDraft] = React.useState<ScopeDraft | null>(null);
  const scopeDraftRef = React.useRef<ScopeDraft | null>(null);
  const updateScopeDraft = (draft: ScopeDraft | null) => {
    scopeDraftRef.current = draft;
    setScopeDraft(draft);
  };

  const commitScope = async (row: Row) => {
    const draft = scopeDraftRef.current;
    updateScopeDraft(null);
    if (!draft || draft.joinId !== row.joinId) return;
    if (sameIdSet(draft.ids, row.alert_structure_ids)) return;

    setRows((prev) => prev.map((r) => (r.joinId === row.joinId ? { ...r, saving: true } : r)));
    try {
      const updated = await updateUserAccountSettings(row.joinId, {
        note: row.note,
        enabled_email_notifications: row.enabled_email_notifications,
        enabled_sms_notifications: row.enabled_sms_notifications,
        alert_structure_ids: draft.ids,
      } as unknown as IUserAccountBase);
      const savedIds = updated.alert_structure_ids || draft.ids;

      setRows((prev) =>
        prev.map((r) =>
          r.joinId === row.joinId
            ? { ...r, alert_structure_ids: savedIds, saving: false, error: null }
            : r,
        ),
      );
      toast.success(`Updated alert properties for ${row.email}: ${describeScope(savedIds)}`, {
        autoClose: 3000,
        closeButton: true,
      });
    } catch (e: any) {
      toast.error(
        `Unable to update alert properties for ${row.email}. ${
          e?.message
            ? `Error: ${e.message}`
            : 'Please try again or contact support if the issue persists.'
        }`,
        {
          autoClose: 6000,
          closeButton: true,
        },
      );
      setRows((prev) => prev.map((r) => (r.joinId === row.joinId ? { ...r, saving: false } : r)));
    }
  };

  // ---- Bulk select state per-flag ----
  const allLoadedRows = rows.filter((r) => !r.loading && !r.error);
  const isEmailFlagSelectedEverywhere = (flag: NotificationFlags) =>
    allLoadedRows.length > 0 &&
    allLoadedRows.every((r) => r.enabled_email_notifications.includes(flag));

  const isSMSFlagSelectedEverywhere = (flag: NotificationFlags) =>
    allLoadedRows.length > 0 &&
    allLoadedRows.every((r) => r.enabled_sms_notifications.includes(flag));

  // ---- Email Cell toggle ----
  const toggleFlag = async (row: Row, flag: NotificationFlags, checked: boolean) => {
    setRows((prev) => prev.map((r) => (r.joinId === row.joinId ? { ...r, saving: true } : r)));

    const currentFlags = new Set(row.enabled_email_notifications);
    if (checked) currentFlags.add(flag);
    else currentFlags.delete(flag);
    const newFlags = Array.from(currentFlags);

    try {
      await updateUserAccountSettings(row.joinId, {
        note: row.note,
        enabled_email_notifications: newFlags,
        enabled_sms_notifications: row.enabled_sms_notifications,
      } as unknown as IUserAccountBase);

      setRows((prev) =>
        prev.map((r) =>
          r.joinId === row.joinId
            ? { ...r, enabled_email_notifications: newFlags, saving: false, error: null }
            : r,
        ),
      );

      // Show success toast for individual updates Email
      toast.success(
        `${checked ? 'Enabled' : 'Disabled'} email ${
          NOTIFICATION_FLAG_LABELS[flag]
        } notifications for ${row.email}`,
        {
          autoClose: 3000,
          closeButton: true,
        },
      );
    } catch (e: any) {
      const action = checked ? 'enable' : 'disable';
      toast.error(
        `Unable to ${action} email ${NOTIFICATION_FLAG_LABELS[flag]} notifications for ${
          row.email
        }. ${
          e?.message
            ? `Error: ${e.message}`
            : 'Please try again or contact support if the issue persists.'
        }`,
        {
          autoClose: 6000,
          closeButton: true,
        },
      );
      setRows((prev) => prev.map((r) => (r.joinId === row.joinId ? { ...r, saving: false } : r)));
    }
  };

  // ---- SMS Cell toggle ----
  const toggleSMSFlag = async (row: Row, flag: NotificationFlags, checked: boolean) => {
    setRows((prev) => prev.map((r) => (r.joinId === row.joinId ? { ...r, saving: true } : r)));

    const currentFlags = new Set(row.enabled_sms_notifications);
    if (checked) currentFlags.add(flag);
    else currentFlags.delete(flag);
    const newFlags = Array.from(currentFlags);

    try {
      await updateUserAccountSettings(row.joinId, {
        note: row.note,
        enabled_email_notifications: row.enabled_email_notifications,
        enabled_sms_notifications: newFlags,
      } as unknown as IUserAccountBase);

      setRows((prev) =>
        prev.map((r) =>
          r.joinId === row.joinId
            ? { ...r, enabled_sms_notifications: newFlags, saving: false, error: null }
            : r,
        ),
      );

      // Show success toast for individual updates SMS
      toast.success(
        `${checked ? 'Enabled' : 'Disabled'} SMS ${
          NOTIFICATION_FLAG_LABELS[flag]
        } notifications for ${row.email}`,
        {
          autoClose: 3000,
          closeButton: true,
        },
      );
    } catch (e: any) {
      const action = checked ? 'enable' : 'disable';
      toast.error(
        `Unable to ${action} SMS ${NOTIFICATION_FLAG_LABELS[flag]} notifications for ${
          row.email
        }. ${
          e?.message
            ? `Error: ${e.message}`
            : 'Please try again or contact support if the issue persists.'
        }`,
        {
          autoClose: 6000,
          closeButton: true,
        },
      );
      setRows((prev) => prev.map((r) => (r.joinId === row.joinId ? { ...r, saving: false } : r)));
    }
  };

  // ---- Bulk toggle for a column ----
  const bulkToggleFlag = async (flag: NotificationFlags) => {
    if (bulkBusy) return;

    const shouldEnable = !isEmailFlagSelectedEverywhere(flag);
    const target = allLoadedRows.filter((r) =>
      shouldEnable
        ? !r.enabled_email_notifications.includes(flag)
        : r.enabled_email_notifications.includes(flag),
    );
    if (target.length === 0) return;

    setBulkBusy(true);

    const actionText = shouldEnable ? 'Enabling' : 'Disabling';
    const userText = target.length === 1 ? '1 user' : `${target.length} users`;

    toast.loading(
      `${actionText} email ${NOTIFICATION_FLAG_LABELS[flag]} notifications for ${userText}...`,
      {
        toastId: `bulk-email-${flag}`,
        closeButton: true,
      },
    );

    let successCount = 0;
    let failureCount = 0;
    const failedUsers: string[] = [];

    try {
      for (const row of target) {
        const next = new Set(row.enabled_email_notifications);
        if (shouldEnable) next.add(flag);
        else next.delete(flag);
        const newFlags = Array.from(next);
        try {
          await updateUserAccountSettings(row.joinId, {
            note: row.note,
            enabled_email_notifications: newFlags,
            enabled_sms_notifications: row.enabled_sms_notifications,
          } as unknown as IUserAccountBase);
          setRows((prev) =>
            prev.map((r) =>
              r.joinId === row.joinId ? { ...r, enabled_email_notifications: newFlags } : r,
            ),
          );
          successCount++;
        } catch (e: any) {
          failureCount++;
          failedUsers.push(row.email);
          // Show individual error for failed users
          toast.error(
            `Failed to ${shouldEnable ? 'enable' : 'disable'} email ${
              NOTIFICATION_FLAG_LABELS[flag]
            } for ${row.email}${e?.message ? `: ${e.message}` : ''}`,
            {
              autoClose: 5000,
              closeButton: true,
            },
          );
        }
      }

      // Update the loading toast with detailed results
      if (failureCount === 0) {
        toast.update(`bulk-email-${flag}`, {
          render: `Successfully ${shouldEnable ? 'enabled' : 'disabled'} email ${
            NOTIFICATION_FLAG_LABELS[flag]
          } notifications for ${successCount === 1 ? '1 user' : `${successCount} users`}`,
          type: 'success',
          isLoading: false,
          autoClose: 4000,
          closeButton: true,
        });
      } else if (successCount === 0) {
        toast.update(`bulk-email-${flag}`, {
          render: `Failed to update email ${NOTIFICATION_FLAG_LABELS[flag]} notifications for all ${
            failureCount === 1 ? '1 user' : `${failureCount} users`
          }. Please check individual error messages above.`,
          type: 'error',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      } else {
        toast.update(`bulk-email-${flag}`, {
          render: `Partially completed: ${successCount} successful, ${failureCount} failed. Email ${
            NOTIFICATION_FLAG_LABELS[flag]
          } notifications ${shouldEnable ? 'enabled' : 'disabled'} for ${
            successCount === 1 ? '1 user' : `${successCount} users`
          }.`,
          type: 'warning',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      }

      if (onRefreshAccount) await onRefreshAccount();
    } catch (error: any) {
      toast.update(`bulk-email-${flag}`, {
        render: `Bulk operation failed unexpectedly. ${
          error?.message ? `Error: ${error.message}` : 'Please try again or contact support.'
        }`,
        type: 'error',
        isLoading: false,
        autoClose: 6000,
        closeButton: true,
      });
    } finally {
      setBulkBusy(false);
    }
  };

  // ---- Bulk SMS toggle for a column ----
  const bulkToggleSMSFlag = async (flag: NotificationFlags) => {
    if (bulkBusy) return;

    const shouldEnable = !isSMSFlagSelectedEverywhere(flag);
    const target = allLoadedRows.filter((r) =>
      shouldEnable
        ? !r.enabled_sms_notifications.includes(flag)
        : r.enabled_sms_notifications.includes(flag),
    );
    if (target.length === 0) return;

    setBulkBusy(true);

    const actionText = shouldEnable ? 'Enabling' : 'Disabling';
    const userText = target.length === 1 ? '1 user' : `${target.length} users`;

    toast.loading(
      `${actionText} SMS ${NOTIFICATION_FLAG_LABELS[flag]} notifications for ${userText}...`,
      {
        toastId: `bulk-sms-${flag}`,
        closeButton: true,
      },
    );

    let successCount = 0;
    let failureCount = 0;
    const failedUsers: string[] = [];

    try {
      for (const row of target) {
        const next = new Set(row.enabled_sms_notifications);
        if (shouldEnable) next.add(flag);
        else next.delete(flag);
        const newFlags = Array.from(next);
        try {
          await updateUserAccountSettings(row.joinId, {
            note: row.note,
            enabled_email_notifications: row.enabled_email_notifications,
            enabled_sms_notifications: newFlags,
          } as unknown as IUserAccountBase);
          setRows((prev) =>
            prev.map((r) =>
              r.joinId === row.joinId ? { ...r, enabled_sms_notifications: newFlags } : r,
            ),
          );
          successCount++;
        } catch (e: any) {
          failureCount++;
          failedUsers.push(row.email);
          // Show individual error for failed users
          toast.error(
            `Failed to ${shouldEnable ? 'enable' : 'disable'} SMS ${
              NOTIFICATION_FLAG_LABELS[flag]
            } for ${row.email}${e?.message ? `: ${e.message}` : ''}`,
            {
              autoClose: 5000,
              closeButton: true,
            },
          );
        }
      }

      // Update the loading toast with detailed results
      if (failureCount === 0) {
        toast.update(`bulk-sms-${flag}`, {
          render: `Successfully ${shouldEnable ? 'enabled' : 'disabled'} SMS ${
            NOTIFICATION_FLAG_LABELS[flag]
          } notifications for ${successCount === 1 ? '1 user' : `${successCount} users`}`,
          type: 'success',
          isLoading: false,
          autoClose: 4000,
          closeButton: true,
        });
      } else if (successCount === 0) {
        toast.update(`bulk-sms-${flag}`, {
          render: `Failed to update SMS ${NOTIFICATION_FLAG_LABELS[flag]} notifications for all ${
            failureCount === 1 ? '1 user' : `${failureCount} users`
          }. Please check individual error messages above.`,
          type: 'error',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      } else {
        toast.update(`bulk-sms-${flag}`, {
          render: `Partially completed: ${successCount} successful, ${failureCount} failed. SMS ${
            NOTIFICATION_FLAG_LABELS[flag]
          } notifications ${shouldEnable ? 'enabled' : 'disabled'} for ${
            successCount === 1 ? '1 user' : `${successCount} users`
          }.`,
          type: 'warning',
          isLoading: false,
          autoClose: 6000,
          closeButton: true,
        });
      }

      if (onRefreshAccount) await onRefreshAccount();
    } catch (error: any) {
      toast.update(`bulk-sms-${flag}`, {
        render: `Bulk operation failed unexpectedly. ${
          error?.message ? `Error: ${error.message}` : 'Please try again or contact support.'
        }`,
        type: 'error',
        isLoading: false,
        autoClose: 6000,
        closeButton: true,
      });
    } finally {
      setBulkBusy(false);
    }
  };

  // ---- Bulk operation state ----
  const [bulkBusy, setBulkBusy] = React.useState(false);

  // ---- Account-level schedule editor ----
  const [tzKey, setTzKey] = React.useState(account.timezone_key || 'US/Eastern');
  const [dow, setDow] = React.useState<number>(account.meter_health_summary_day_of_week ?? 2); // default Tuesday
  const [hour, setHour] = React.useState<number>(account.meter_health_summary_hour ?? 17); // default 17:00
  const [savingSchedule, setSavingSchedule] = React.useState(false);

  const saveSchedule = async () => {
    try {
      setSavingSchedule(true);
      await updateAccount(account.id, {
        timezone_key: tzKey,
        meter_health_summary_enabled: true, // Always enabled
        meter_health_summary_day_of_week: dow,
        meter_health_summary_hour: hour,
      });

      const dayLabel = DOW_LABELS.find((d) => d.value === dow)?.label || 'Unknown';
      const timeLabel = `${hour.toString().padStart(2, '0')}:00`;

      toast.success(
        `Meter/Device Health Summary schedule updated successfully! Summaries will be sent every ${dayLabel} at ${timeLabel} (${tzKey} timezone).`,
        {
          autoClose: 5000,
          closeButton: true,
        },
      );
      if (onRefreshAccount) await onRefreshAccount();
    } catch (e: any) {
      toast.error(
        `Unable to update Device Health Summary schedule. ${
          e?.message
            ? `Error: ${e.message}`
            : 'Please verify your settings and try again, or contact support if the issue persists.'
        }`,
        {
          autoClose: 6000,
          closeButton: true,
        },
      );
    } finally {
      setSavingSchedule(false);
    }
  };

  // ---- Render ----
  const visibleRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Stack spacing={3}>
      {/* Account-level Meter/Device Health Summary schedule */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Device Health Summary Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Device Health Summaries are <strong>enabled for this account</strong>. Configure the
            schedule below and manage which users receive summaries via the "Device Health Summary"
            column in the table.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <React.Suspense fallback={<CircularProgress size={24} />}>
              <TimeZoneSelector
                value={tzKey}
                onChange={(value: string) => setTzKey(value)}
                sx={{ minWidth: 220, margin: 0 }}
              />
            </React.Suspense>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="dow-label">Day of Week</InputLabel>
              <Select
                labelId="dow-label"
                label="Day of Week"
                value={dow}
                onChange={(e) => setDow(Number(e.target.value))}
              >
                {DOW_LABELS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel id="hour-label">Hour (24h)</InputLabel>
              <Select
                labelId="hour-label"
                label="Hour (24h)"
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={h}>
                    {h.toString().padStart(2, '0')}:00
                    {h === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {' '}
                        (Midnight)
                      </Typography>
                    )}
                    {h === 12 && (
                      <Typography variant="body2" color="text.secondary">
                        {'(Noon)'}
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <SWLoadingButton variant="contained" loading={savingSchedule} onClick={saveSchedule}>
              Save Schedule
            </SWLoadingButton>
          </Stack>
        </CardContent>
      </Card>

      {/* Users table */}
      <Paper variant="outlined" sx={{ borderColor: 'divider', borderWidth: 2, borderRadius: 2 }}>
        <Box px={3} py={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Team Notification Settings</Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell sx={{ minWidth: '220px' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                      {ALERT_PROPERTIES_LABEL}
                    </Typography>
                    <Tooltip title={ALERT_PROPERTIES_DESCRIPTION}>
                      <IconButton>
                        <InfoOutlinedIcon sx={{ fontSize: '17px', color: '#555', mb: 0.25 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                {FLAG_ORDER.map((flag) => (
                  <TableCell key={flag} align="center" sx={{ minWidth: '160px' }}>
                    <Stack direction="column" alignItems="center" spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                          {NOTIFICATION_FLAG_LABELS[flag]}
                        </Typography>
                        <Tooltip title={NOTIFICATION_FLAG_DESCRIPTIONS[flag]}>
                          <IconButton>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: '17px',
                                color: '#555',
                                mb: 0.25,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Stack direction="column" alignItems="center" spacing={0.5}>
                          <EmailIcon fontSize="small" />
                          <Checkbox
                            size="small"
                            checked={isEmailFlagSelectedEverywhere(flag)}
                            onChange={() => bulkToggleFlag(flag)}
                            disabled={bulkBusy}
                          />
                        </Stack>
                        {flag !== NotificationFlags.METER_HEALTH_SUMMARY && (
                          <Stack direction="column" alignItems="center" spacing={0.5}>
                            <SmsIcon fontSize="small" />
                            <Checkbox
                              size="small"
                              checked={isSMSFlagSelectedEverywhere(flag)}
                              onChange={() => bulkToggleSMSFlag(flag)}
                              disabled={bulkBusy}
                            />
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2 + FLAG_ORDER.length} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No confirmed users.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => {
                  const isCurrentUser = row.joinId === currentUserId;
                  return (
                    <TableRow
                      key={row.joinId}
                      hover
                      sx={{
                        backgroundColor: isCurrentUser ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                        '&:hover': {
                          backgroundColor: isCurrentUser
                            ? 'rgba(25, 118, 210, 0.12)'
                            : 'action.hover',
                        },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {row.loading && <CircularProgress size={16} />}
                          {isCurrentUser && (
                            <Chip
                              label="You"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                              }}
                              color="primary"
                            />
                          )}
                          <Typography variant="body2">{row.email}</Typography>
                          {row.user_status === 'FORCE_CHANGE_PASSWORD' && (
                            <Chip
                              label="Pending Setup"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        </Stack>
                        {row.error && (
                          <Typography variant="caption" color="error">
                            {row.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.loading || structuresLoading ? (
                          <CircularProgress size={18} />
                        ) : structuresError ? (
                          <Typography variant="caption" color="error">
                            Properties unavailable
                          </Typography>
                        ) : (
                          (() => {
                            const selectedIds =
                              scopeDraft?.joinId === row.joinId
                                ? scopeDraft.ids
                                : row.alert_structure_ids;
                            return (
                              <FormControl size="small" fullWidth>
                                <Select
                                  multiple
                                  displayEmpty
                                  value={selectedIds}
                                  inputProps={{ 'aria-label': `Alert properties for ${row.email}` }}
                                  renderValue={(ids) => describeScope(ids as string[])}
                                  onOpen={() =>
                                    updateScopeDraft({
                                      joinId: row.joinId,
                                      ids: row.alert_structure_ids,
                                    })
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    updateScopeDraft({
                                      joinId: row.joinId,
                                      ids: typeof value === 'string' ? value.split(',') : value,
                                    });
                                  }}
                                  onClose={() => commitScope(row)}
                                  disabled={row.saving || bulkBusy}
                                >
                                  {structures.map((structure) => (
                                    <MenuItem key={structure.id} value={structure.id}>
                                      <Checkbox
                                        size="small"
                                        checked={selectedIds.includes(structure.id)}
                                      />
                                      <ListItemText primary={structure.name} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            );
                          })()
                        )}
                      </TableCell>
                      {FLAG_ORDER.map((flag) => {
                        const emailChecked = row.enabled_email_notifications.includes(flag);
                        const smsChecked = row.enabled_sms_notifications.includes(flag);
                        return (
                          <TableCell key={flag} align="center">
                            {row.loading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Stack direction="row" spacing={2} justifyContent="center">
                                <Checkbox
                                  checked={emailChecked}
                                  onChange={(e) => toggleFlag(row, flag, e.target.checked)}
                                  disabled={row.saving || bulkBusy}
                                  size="small"
                                  // sx={{ pl: 7, pr: 4 }}
                                />
                                {flag !== NotificationFlags.METER_HEALTH_SUMMARY && (
                                  <Checkbox
                                    checked={smsChecked}
                                    onChange={(e) => toggleSMSFlag(row, flag, e.target.checked)}
                                    disabled={row.saving || bulkBusy}
                                    size="small"
                                  />
                                )}
                              </Stack>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Stack>
  );
}
